/**
 * Configureerbaar tegel-dashboard — DTO's + Zod-schemas.
 *
 * Conform docs/specs/22-configureerbaar-dashboard.md §Types + Fase 1. Tegel-
 * definities, per-user dashboards, tegel-instanties + layout-mutaties. De
 * tegel-fetchers, API-routes en grid-UI komen in PR #E; deze module levert de
 * gedeelde contracten + de seed-input (tegel-keys + categorieën).
 */

import { z } from 'zod'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const DASHBOARD_TILE_CATEGORIES = [
  'OPERATIONEEL',
  'HR_VERZUIM',
  'FINANCIEEL',
  'CAPACITEIT',
  'RAPPORTAGE',
  'OVERIG',
] as const
export type DashboardTileCategory = (typeof DASHBOARD_TILE_CATEGORIES)[number]

/// Alle 28 tegel-keys (spec 22 §2.1). Mirror van Prisma `DashboardTileKey`.
export const DASHBOARD_TILE_KEYS = [
  // Operationeel
  'PLANNING_VANDAAG',
  'WERKBONNEN_OPEN',
  'WERKBONNEN_TE_ACCORDEREN',
  'UREN_ONTBREKEN',
  'AANDACHTSPUNTEN',
  'SNELACTIES',
  // HR / Verzuim / Verlof
  'VERLOFAANVRAGEN',
  'ZIEKMELDINGEN',
  'CERTIFICATEN_VERLOPEN',
  'HEADCOUNT',
  'INSTROOM_UITSTROOM',
  'FREQUENT_VERZUIM',
  'CONTRACTEN_VERLOPEN',
  // Financieel
  'OMZET_DEZE_MAAND',
  'OPENSTAANDE_FACTUREN',
  'MARGE_PER_KLANT',
  'BETALINGSTERMIJNEN',
  'UREN_NIET_GEFACTUREERD',
  // Capaciteit
  'CAPACITEIT_DEZE_WEEK',
  'BEZETTINGSGRAAD_TREND',
  'VERLOFFORECAST',
  // Rapportage / Trends
  'TRENDS',
  'PRODUCTIVITEIT',
  'TOP_KLANTEN',
  'TOP_PROJECTEN',
  // Overig
  'APP_MELDINGEN',
  'NOTIFICATIES_RECENT',
  'PAYROLL_SAMENVATTING',
  // batch-66 inkoop-afwijkingen (FO-497) — boetes per medewerker en voertuig.
  'BOETES_RIJGEDRAG',
  // batch-69 km-balans (FO-515) — privé-indicatie boven de drempel (aantal medewerkers).
  'KILOMETERBALANS_DREMPEL',
] as const
export type DashboardTileKey = (typeof DASHBOARD_TILE_KEYS)[number]

export const DASHBOARD_TILE_CATEGORY_LABELS: Record<DashboardTileCategory, string> = {
  OPERATIONEEL: 'Operationeel',
  HR_VERZUIM: 'HR & Verzuim',
  FINANCIEEL: 'Financieel',
  CAPACITEIT: 'Capaciteit',
  RAPPORTAGE: 'Rapportage',
  OVERIG: 'Overig',
}

// ─── Request schemas ─────────────────────────────────────────────────────────

export const tileConfigSchema = z.object({
  titel: z.string().max(100).optional(),
  periode: z.enum(['vandaag', 'deze-week', 'deze-maand', 'dit-kwartaal', 'dit-jaar']).optional(),
  entityId: z.string().optional(),
  teamId: z.string().optional(), // 'mijn-team' of specifiek ID
  medewerkerIds: z.array(z.string()).optional(),
  klantId: z.string().optional(),
  status: z.array(z.string()).optional(),
  weergave: z.enum(['aantal', 'lijst', 'grafiek']).optional(),
  maxItems: z.number().int().min(1).max(50).optional(),
  verversInterval: z.number().int().min(0).max(3600).optional(),
})
export type TileConfig = z.infer<typeof tileConfigSchema>

export const layoutTileSchema = z.object({
  tileDefinitionId: z.string(),
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(6),
  config: tileConfigSchema.default({}),
})

export const updateLayoutBodySchema = z.object({
  tiles: z.array(layoutTileSchema).max(30), // max 30 tegels per dashboard
})
export type UpdateLayoutBody = z.infer<typeof updateLayoutBodySchema>

export const addTileBodySchema = z.object({
  tileDefinitionId: z.string(),
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(12).optional(),
  h: z.number().int().min(1).max(6).optional(),
  config: tileConfigSchema.default({}),
})
export type AddTileBody = z.infer<typeof addTileBodySchema>

export const updateTileBodySchema = z.object({
  x: z.number().int().min(0).max(11).optional(),
  y: z.number().int().min(0).optional(),
  w: z.number().int().min(1).max(12).optional(),
  h: z.number().int().min(1).max(6).optional(),
  config: tileConfigSchema.optional(),
})
export type UpdateTileBody = z.infer<typeof updateTileBodySchema>

// ─── Response DTO's ──────────────────────────────────────────────────────────

export interface TileDefinitionDTO {
  id: string
  key: string
  name: string
  description: string | null
  category: DashboardTileCategory
  defaultW: number
  defaultH: number
  minW: number
  minH: number
  maxW: number
  maxH: number
  availableFilters: string[]
}

export interface TileInstanceDTO {
  id: string
  tileDefinitionId: string
  definition: TileDefinitionDTO
  x: number
  y: number
  w: number
  h: number
  config: TileConfig
}

export interface UserDashboardDTO {
  id: string
  name: string
  templateName: string | null
  tiles: TileInstanceDTO[]
}

export interface TileDataDTO {
  tileKey: string
  data: unknown // per tegel-type specifiek
  fetchedAt: string // ISO timestamp
}

// Gegroepeerde tegel-bibliotheek
export interface TileLibraryDTO {
  categories: Array<{
    category: DashboardTileCategory
    label: string
    tiles: TileDefinitionDTO[]
  }>
}

// ─── Admin template-beheer (spec 22 Fase 9) ─────────────────────────────────────

/** UserRole-waarden waaraan een template gekoppeld kan worden (mirror Prisma). */
export const DASHBOARD_TEMPLATE_ROLES = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
  'ADMIN',
] as const
export type DashboardTemplateRole = (typeof DASHBOARD_TEMPLATE_ROLES)[number]

export const templateTileSchema = z.object({
  tileDefinitionId: z.string(),
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(6),
  config: tileConfigSchema.default({}),
})

export const dashboardTemplateCreateSchema = z.object({
  name: z.string().min(1).max(100),
  role: z.enum(DASHBOARD_TEMPLATE_ROLES),
  isDefault: z.boolean().default(true),
  tiles: z.array(templateTileSchema).max(30).default([]),
})
export type DashboardTemplateCreateBody = z.infer<typeof dashboardTemplateCreateSchema>

export const dashboardTemplateUpdateSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  role: z.enum(DASHBOARD_TEMPLATE_ROLES).optional(),
  isDefault: z.boolean().optional(),
  tiles: z.array(templateTileSchema).max(30).optional(),
})
export type DashboardTemplateUpdateBody = z.infer<typeof dashboardTemplateUpdateSchema>

export interface TemplateTileDTO {
  id: string
  tileDefinitionId: string
  definition: TileDefinitionDTO
  x: number
  y: number
  w: number
  h: number
  config: TileConfig
}

export interface DashboardTemplateDTO {
  id: string
  name: string
  role: DashboardTemplateRole
  isDefault: boolean
  tileCount: number
  tiles: TemplateTileDTO[]
  createdAt: string
  updatedAt: string
}
